import { useRef, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLatest } from 'react-use';
import { Form, Input } from '@tencent/tea-component';

import JoiningMeeting from '@component/joining-meeting';
import WaitingRoom from '@component/waiting-room';
import wemeetClient from '@utils/wemeetClient';

import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  getMeetingInfoInput,
  setMeetingInfoInput,
  setIfInMeeting,
  setIsHostInMeeting,
  setMeetingSettings,
} from '@store/meeting/meeting.slice';
import EnteringHeader from '@component/entering-header';
import { BOOL, MeetingCode } from '@tencent/wemeet-websdk-next';
import { openDialog } from '@component/dialog';

import { JoinMeetingError } from 'src/type/error';
import { getIfShowWaitingRoom, showWaitingRoom } from '@store/ui/ui.slice';
import { filterDigit } from '@utils/string';
import {
  getVoovAuthInfo,
  getXMPPLoginState,
  XmppLoginStateEnum,
  getFixVideoSwitch,
} from '@store/authentication/authentication.slice';
import { gotoHomePage, gotoHomePageWithMeetingCode, gotoLoginPage } from '@utils/navigate';
import { setSelfUserMetaInfo } from '@store/user/user.slice';
import { captureException } from '@utils/sentry';

import ISSUE_NAME from '@constant/issue-name';

import {
  reportXMPPJoinFail,
  setAegisExt,
  reportRTCEnterRoomSuccess,
  reportRTCEnterRoomFail,
  reportTimeStartJoinMeetingTotal,
  reportTimeEndTRTCEnterRoom,
  reportTimeEndJoinMeetingTotal,
  reportUserEnter,
  reportGuestEnter,
  reportCreatorEnter,
} from 'src/report/aegis';

import './EnteringPage.scss';
import useAutoJoin from './useAutoJoin';
import { xmppLogin } from '@store/authentication/authentication.trunk';
import { GUEST_APP_ID } from '@constant/index';
import { fetchAndCheckMeetingInfo } from '@store/meeting/metting.trunk';

/**
 * 入会 loading 页
 *
 * 从会议入口页，点击进会，先跳到这里再处理 进会的逻辑
 * 用户直接打开 /r/${meetingCode}, 先通过路由守卫进入该页面，再处理进会的逻辑
 *
 * 以上两种情况都在 EnteringPage 组件进行进会
 * 而在这之前，Framework 组件会先登录 voov、建立 xmpp 链接
 *
 * 因为进会前是需要 voovLogin, 建立 xmpp 链接的。
 * 做这两步肯定是在 EnteringPage 渲染前，但是这两个步骤异步的。
 * EnteringPage 渲染的时候这两步肯定还没有完成
 *
 * 所以，EnteringPage 组件初始化后等待 xmpp 建立链接完成后，立刻进行进会操作
 * （每 20毫秒，查询一次 xmpp 建立连接的完成情况）
 *
 */
export default function EnteringPage() {
  const dispatch = useAppDispatch();

  const meetingInfoInput = useAppSelector(getMeetingInfoInput);
  const voovAuthInfo = useAppSelector(getVoovAuthInfo);
  const fixVideoSwitch = useAppSelector(getFixVideoSwitch);
  const ifShowWaitingRoom = useAppSelector(getIfShowWaitingRoom);
  const xmppLoginState = useAppSelector(getXMPPLoginState);

  const { t } = useTranslation();

  const { meetingCode: meetingCodeParams = '' } = useParams<'meetingCode'>();
  const meetingCode = filterDigit(meetingCodeParams);

  const latestPassword = useLatest(meetingInfoInput.password);
  const [ifSHowPasswordError, showPasswordError] = useState(false);
  const joinTimeout = useRef(0);

  useAutoJoin(startJoin);

  useEffect(() => {
    // 计时开始：进房总耗时
    reportTimeStartJoinMeetingTotal();
  }, []);

  /**
   * 等待 voov 登录登录成功后再进行进会流程
   *
   * 进会流程都在 enteringPage.tsx 完成
   * 进会第一步： 1. xmpp 建立连接（xmpp 登录）
   * 进会第二步： 2. 查询会议信息
   * 进会第三步： 3. 进入具体的会议
   */
  async function startJoin() {
    if (voovAuthInfo?.appid === GUEST_APP_ID && xmppLoginState === XmppLoginStateEnum.idle) {
      // 游客直接打开 /r/会议号的情况, 调回 /r
      gotoHomePageWithMeetingCode(meetingCode);
      return;
    }
    const xmppResult = await dispatch(xmppLogin());

    if (xmppResult.payload === XmppLoginStateEnum.succeeded) {
      /**
       *
       * 进会逻辑
       *
       * 1. 查询会议信息
       *  => 1.1 发起加入会议请求
       *  => 1.2 密码弹窗
       *
       * 2. 发起加入会议请求
       *  => 2.1 成功进入会议
       *  => 2.2 进入等候室
       */

      const result = await dispatch(fetchAndCheckMeetingInfo(meetingCode));
      // @ts-ignore
      const { checkResult } = result.payload;
      // @ts-ignore
      const meetingInfo = result.payload?.meetingInfo;

      const isCreator = meetingInfo?.creator?.['@app_uid'] === voovAuthInfo?.userid;
      console.log('isCreator:', isCreator);
      //  需要密码的弹窗
      const needPassword = meetingInfo.need_auth === BOOL.true;
      if (needPassword && !ifShowWaitingRoom) {
        openPasswordDialog.current(isCreator);
        return false;
      }

      if (checkResult) {
        joinMeeting(meetingCode, '', isCreator);
      }
    }
  }

  const goBackToJoinFormPage = () => {
    gotoHomePage();
  };

  /**
   * FIXME:
   * 不能用 openDialog 的方式打开密码弹窗
   * 因为使用 API 打开对话框，实际上会断掉数据流
   *
   * TODO 看了下应该可以的，待重构一波
   *
   * 在 EnteringPage 上的数据更新将无法触发 Dialog 的更新
   *
   * openDialog 只适合简单的情况
   *
   * TODO:
   * 1. 密码错误的时候不要关闭弹窗再打开弹窗
   * 2. 重新输入密码的时候就需要去掉密码错误的提示
   */
  const openPasswordDialog = useLatest((isCreator: any) => {
    openDialog({
      dialogId: 'inputPassword',
      title: t('请输入密码'),
      content: (
        <div>
          {t('会议已设置密码，请联系主持人获取')}
          <Form.Control
            status={ifSHowPasswordError ? 'error' : undefined}
            message={ifSHowPasswordError ? t('密码不正确') : undefined}
            showStatusIcon={false}
          >
            <Input.Password
              className="entering-page__password"
              onChange={(value) => {
                dispatch(
                  setMeetingInfoInput({
                    password: value,
                  }),
                );
              }}
              rules={false}
              placeholder={t('请输入密码')}
            />
          </Form.Control>
        </div>
      ),
      okText: t('确定'),
      onOk: () => {
        joinMeeting(meetingCode, latestPassword.current, isCreator);
      },
      onCancel: goBackToJoinFormPage,
    });
  });

  const joinMeeting = async (meetingCode: MeetingCode, password?: string, isCreator?: boolean) => {
    startJoinTimeoutFn();

    try {
      const joinResult = await wemeetClient.meetingCtrl.joinMeeting({
        meeting_code: meetingCode,
        nickname: meetingInfoInput.nickname || voovAuthInfo?.nickname || '',
        password,
        audio_on: meetingInfoInput.enableAudio,
        video_on: meetingInfoInput.enableVideo,
        avatar_token: voovAuthInfo!.avatarToken,
        avatar_type: voovAuthInfo!.avatarType,
        user_district: voovAuthInfo?.user_district,
        fixVideoSwitch,
      });

      const meetingSetting = await wemeetClient.meetingCtrl.queryMeetingSetting();

      dispatch(setMeetingSettings(meetingSetting));

      // 计时结束：TRTC 进房耗时
      reportTimeEndTRTCEnterRoom();

      // 进会完全成功了
      cancelJoinTimeoutFn();

      dispatch(
        setSelfUserMetaInfo({
          av_identifier: joinResult.av_identifier,
          av_user_id: joinResult.av_user_id,
          av_room_id: joinResult.av_room_id,
        }),
      );

      dispatch(setIfInMeeting(true));

      // aegis 设置通用变量
      setAegisExt({
        ext1: joinResult.av_user_id,
        ext2: joinResult.av_room_id,
        ext3: joinResult.meeting_code,
      });

      if (voovAuthInfo?.appid === GUEST_APP_ID) {
        reportGuestEnter();
      } else {
        reportUserEnter(!!isCreator);
      }
      if (isCreator) {
        reportCreatorEnter();
      }

      reportRTCEnterRoomSuccess();
    } catch (e: any) {
      console.log('应用层捕获到加入会议出错', e);
      cancelJoinTimeoutFn();

      // 1. 密码错误
      if (e.code === JoinMeetingError.wrongPassword) {
        showPasswordError(true);
        openPasswordDialog.current(isCreator);
        return;
      }

      // 2. 仅企业内部成员可以入会
      if (e.code === JoinMeetingError.onlyCorp) {
        openDialog({
          title: t('请登录企业内部账号'),
          content: t('该会议仅允许企业内部用户加入，请登录对应企业账号后，再重新加入'),
          okText: t('切换账号'),
          // FIXME: 跳转到切换账号的页面，但是web现在没有，直接跳首页
          onOk: () => {
            gotoLoginPage();
          },
          onCancel: goBackToJoinFormPage,
        });
        return;
      }
      // 黑名单
      if (e.code === JoinMeetingError.banList) {
        openDialog({
          title: t('加入会议失败'),
          content: t('您已被移出会议，加入会议失败\n如需加入，请联系会议主持人'),
          onOk: goBackToJoinFormPage,
        });
        return;
      }
      if (e.code === JoinMeetingError.webInar) {
        openDialog({
          title: t('错误'),
          content: t('暂不支持加入该会议，请使用客户端版本重试'),
          onOk: goBackToJoinFormPage,
        });
        return;
      }

      if (e.code === JoinMeetingError.timeout) {
        openDialog({
          title: t('错误'),
          content: t('加入会议超时了。请尝试刷新页面后重新进会。'),
          okText: t('确定'),
          onOk: goBackToJoinFormPage,
        });
        return;
      }

      // TODO: 统一错误码
      if (e.code === 10007) {
        console.log('showWaitingRoom', e.data);
        const hostStatus = e.data?.host_status?.status;
        dispatch(showWaitingRoom());
        dispatch(setIsHostInMeeting(hostStatus === '1'));

        // FIXME: 这里应该是取消，而不是计时结束
        // 计时结束：进房总耗时
        reportTimeEndJoinMeetingTotal();

        return;
      }

      // 以下处理未知错误、没有标记的错误
      // XMPP:
      // TRTC: 鉴权失败，连接超时等
      if (e.name === 'RtcError') {
        reportRTCEnterRoomFail(e);
        openDialog({
          title: t('错误'),
          content: t('加入会议超时了。请尝试刷新页面后重新进会。'),
          okText: t('确定'),
          onOk: goBackToJoinFormPage,
        });
      } else {
        openDialog({
          title: t('错误'),
          content: e.msg,
          okText: t('确定'),
          onOk: goBackToJoinFormPage,
        });
        reportXMPPJoinFail(e);
      }
    }
  };
  const startJoinTimeoutFn = () => {
    if (joinTimeout.current) {
      window.clearTimeout(joinTimeout.current);
    }

    joinTimeout.current = window.setTimeout(() => {
      openDialog({
        title: t('错误'),
        content: t('加入会议超时了。请尝试刷新页面后重新进会。'),
        okText: t('确定'),
        onOk: goBackToJoinFormPage,
      });

      captureException(ISSUE_NAME.JOIN_FAIL_TIMEOUT, 'EnteringPage', {
        FIXME: '这里好像获取不到 TRTC 的错误',
      });
    }, 30 * 1000);
  };
  const cancelJoinTimeoutFn = () => {
    clearTimeout(joinTimeout.current);
    joinTimeout.current = 0;
  };

  return (
    <div>
      {/* 顶部 */}
      <EnteringHeader />

      {/* joining meeting */}
      {!ifShowWaitingRoom && <JoiningMeeting />}

      {/* 等候室 */}
      {ifShowWaitingRoom && <WaitingRoom />}
    </div>
  );
}
